import type { PresenceStatus } from '../types/chat'
import { formatRelativeLastActive } from './last-active'
import { formatRelativeTime } from './format-relative-time'

/**
 * Presence dot colours. Online → green, idle → orange. OFFLINE has NO indicator
 * at all (empty string): we never show a red/offline dot anywhere in the app —
 * absence of a dot conveys offline. Always gate rendering on a non-empty class
 * (see {@link hasPresenceDot}) so OFFLINE renders nothing.
 */
export const PRESENCE_DOT_CLASS: Record<PresenceStatus, string> = {
  ACTIVE: 'bg-emerald-500',
  IDLE: 'bg-amber-500',
  OFFLINE: '',
}

/** Whether a presence status should render a visible dot (false for OFFLINE). */
export function hasPresenceDot(status: PresenceStatus | null | undefined): boolean {
  return status === 'ACTIVE' || status === 'IDLE'
}

export const PRESENCE_LABEL: Record<PresenceStatus, string> = {
  ACTIVE: 'Active',
  IDLE: 'Idle',
  OFFLINE: 'Offline',
}

export function presenceLabel(status: PresenceStatus): string {
  return PRESENCE_LABEL[status] ?? PRESENCE_LABEL.OFFLINE
}

/**
 * User-facing presence text. Online/idle keep their live labels; offline shows
 * a relative "active … ago" string when a last-active timestamp is available.
 */
export function presenceStatusLabel(
  status: PresenceStatus,
  lastActiveAt?: string | number | null,
): string {
  if (status === 'OFFLINE') {
    return formatRelativeLastActive(lastActiveAt) ?? presenceLabel(status)
  }
  return presenceLabel(status)
}

/** Thread metadata used to infer peer activity when profile lastActiveAt is missing. */
export type MessageActivityHint = {
  lastSentAt?: number
  lastMessageSenderId?: string
  /** Latest message timestamp from this peer in the open thread (any position). */
  peerLastMessageAt?: number
}

/**
 * When a peer sent the latest message in a thread, treat that time as activity
 * even if their profile row was never stamped (e.g. message before deploy).
 */
export function resolveEffectiveLastActiveAt(
  peerUserId: string | null | undefined,
  profileLastActiveAt: number | null,
  hint?: MessageActivityHint | null,
): number | null {
  let best = profileLastActiveAt

  if (peerUserId && hint?.lastMessageSenderId === peerUserId && hint.lastSentAt != null) {
    if (best == null || hint.lastSentAt > best) best = hint.lastSentAt
  }

  if (peerUserId && hint?.peerLastMessageAt != null) {
    if (best == null || hint.peerLastMessageAt > best) best = hint.peerLastMessageAt
  }

  return best
}

export function resolvePeerDisplayPresence(
  peer:
    | {
        userId: string
        presenceStatus?: PresenceStatus | null
        lastActiveAt?: string | number | null
      }
    | null
    | undefined,
  profileLastActiveAt: number | null,
  profilePresence: PresenceStatus,
  hint?: MessageActivityHint | null,
  now: number = Date.now(),
): { presence: PresenceStatus; lastActiveAt: number | null } {
  const lastActiveAt = resolveEffectiveLastActiveAt(
    peer?.userId,
    profileLastActiveAt,
    hint,
  )
  return {
    lastActiveAt,
    presence: derivePresence(profilePresence, lastActiveAt, now),
  }
}

export function normalizePresence(
  status: PresenceStatus | null | undefined,
): PresenceStatus {
  if (status === 'ACTIVE' || status === 'IDLE' || status === 'OFFLINE') return status
  return 'OFFLINE'
}

/**
 * Presence thresholds:
 * - ACTIVE: used the app within the last 5 minutes
 * - IDLE: 5–30 minutes since last activity
 * - OFFLINE: 30+ minutes, or deliberate sign-out (publishes OFFLINE explicitly)
 * Writers publish heartbeats; READERS derive the effective status from the
 * last reported status + how stale its lastActiveAt timestamp is. This avoids
 * writers racing to mark each other OFFLINE and gives a natural grace period
 * when the app is closed or backgrounded.
 */
/** Reported ACTIVE stays ACTIVE for this long after the last heartbeat. */
export const PRESENCE_ACTIVE_GRACE_MS = 5 * 60_000
/** Anyone unseen for this long is OFFLINE regardless of their last reported status. */
export const PRESENCE_OFFLINE_AFTER_MS = 30 * 60_000

export function derivePresence(
  status: PresenceStatus | null | undefined,
  lastActiveAt: string | number | null | undefined,
  now: number = Date.now(),
): PresenceStatus {
  const reported = normalizePresence(status)

  if (lastActiveAt != null) {
    const ts = typeof lastActiveAt === 'number' ? lastActiveAt : Date.parse(lastActiveAt)
    if (Number.isFinite(ts)) {
      const age = now - ts
      if (age > PRESENCE_OFFLINE_AFTER_MS) return 'OFFLINE'
      if (age > PRESENCE_ACTIVE_GRACE_MS) return 'IDLE'
      return 'ACTIVE'
    }
  }

  // No usable timestamp — only trust explicit OFFLINE (sign-out clears lastActiveAt).
  if (reported === 'OFFLINE') return 'OFFLINE'
  return reported === 'ACTIVE' ? 'ACTIVE' : 'IDLE'
}

/** Compact last-active label for status tiles (e.g. "Yesterday", "Active now"). */
export function formatPeerLastActive(
  lastActiveAt: number | null,
  presence: PresenceStatus,
): string | null {
  if (lastActiveAt != null) {
    const formatted = formatRelativeTime(lastActiveAt)
    if (formatted) return formatted
  }
  if (presence === 'ACTIVE') return 'Active now'
  return null
}

/** Human label like "Active now", "Last active 5m ago", "Last active 2d ago". */
export function formatLastActive(
  lastActiveAt: string | number | null | undefined,
  status: PresenceStatus,
  now: number = Date.now(),
): string | null {
  if (status === 'ACTIVE') return 'Active now'
  if (lastActiveAt == null) return null

  const ts = typeof lastActiveAt === 'number' ? lastActiveAt : Date.parse(lastActiveAt)
  if (!Number.isFinite(ts)) return null

  const age = Math.max(0, now - ts)
  const minutes = Math.floor(age / 60_000)
  if (minutes < 1) return 'Last active just now'
  if (minutes < 60) return `Last active ${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `Last active ${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `Last active ${days}d ago`
  return `Last active ${new Date(ts).toLocaleDateString()}`
}
